import {
	hasCancelledStatus,
	hasExpiredPaidSubscription,
	hasFailedPaymentStatus,
	hasOrganizationPaidAccess
} from '@/utils/subscriptionAccess'
import { isTeamAssessmentRoute } from '@/utils/assessmentInvitation'

/**
 * Check if a subscription is truly expired (not just cancelled but still paid-through).
 * Matches the logic in SubscriptionBanner.vue isSubscriptionExpired()
 * 
 * @param {Object} org - The organization object with subscription data
 * @returns {boolean} - True if subscription is expired, false if still valid
 */
function isSubscriptionTrulyExpired(org) {
    if (!org) {
        return false
    }

    const subscription = org.subscription

    if (hasHardBlockedPaymentStatus(org)) {
        return true
    }

    if (org.is_in_trial === true && org.trial_expired !== true) {
        return false
    }

    if (hasPaidAccess(org) && subscription?.is_free !== true) {
        return false
    }
    
    // After trusted access signals, failed/cancelled statuses are expired.
    if (hasFailedPaymentStatus(org.payment_status) || hasCancelledStatus(org.payment_status)) {
        return true
    }

    // Check subscription stripe_status for non-recovery payment states.
    if (subscription) {
        if (hasExpiredPaidSubscription(subscription)) {
            return true
        }
    }
    
    if (org.payment_status === 'paid') {
        return true
    }
    
    // If we get here with trial payment_status but trial_expired flag, it's expired
    if (org.payment_status === 'trial' && org.trial_expired === true) {
        return true
    }

    if (org.payment_status === 'trial') {
        return !hasValidSubscription(org)
    }
    
    return false
}

/**
 * Check if subscription is still valid (active or cancelled but still paid-through)
 * 
 * @param {Object} org - The organization object with subscription data
 * @returns {boolean} - True if subscription is valid, false if expired
 */
function hasValidSubscription(org) {
    if (!org) return false

    if (hasHardBlockedPaymentStatus(org)) return false
    
    // Active trial
    if (org.is_in_trial && org.trial_expired !== true) return true
    
    return hasPaidAccess(org)
}

function hasPaidAccess(org) {
    return hasOrganizationPaidAccess(org)
}

function hasHardBlockedPaymentStatus(org) {
    return org?.payment_status === 'incomplete' || org?.payment_status === 'paused'
}

export default function({ store, route, redirect }) {
    const isTeamAssessment = isTeamAssessmentRoute(route)

    if (isTeamAssessment) {
        return
    }

    // Get organization from Vuex store
    const user = store.state.user.user
    const org = user?.current_organization

    if (
        org &&
        route.path.startsWith('/organization/subscription-expired') &&
        hasValidSubscription(org) &&
        !isSubscriptionTrulyExpired(org)
    ) {
        return redirect('/dashboard')
    }

    if (
        org &&
        route.path.startsWith('/organization/trial-expired') &&
        hasValidSubscription(org) &&
        !isSubscriptionTrulyExpired(org)
    ) {
        return redirect('/dashboard')
    }
    
    // Allow settings (profile) and pricing pages, plus payment status pages
    const allowedPaths = [
        '/organization/profile',
        '/organization/pricing',
        '/organization/trial-expired',
        '/organization/subscription-expired',
        '/organization/payment-past-due',
        '/organization/payment-incomplete',
        '/organization/payment-paused'
    ]
    
    if (allowedPaths.some(path => route.path === path || route.path.startsWith(path + '/'))) {
        return
    }
    
    // Block organizations with problematic payment statuses unless backend
    // still reports paid-through access for the recovery window.
    if (org && org.payment_status === 'past_due' && !hasValidSubscription(org)) {
        return redirect('/organization/payment-past-due')
    }
    
    if (org && org.payment_status === 'incomplete') {
        return redirect('/organization/payment-incomplete')
    }
    
    if (org && org.payment_status === 'paused') {
        return redirect('/organization/payment-paused')
    }
    
    // Block trial-expired orgs from accessing protected pages
    if (org && org.trial_expired === true && !hasValidSubscription(org)) {
        return redirect('/organization/trial-expired')
    }

    // Block orgs with expired subscriptions from accessing protected pages
    if (org && isSubscriptionTrulyExpired(org)) {
        return redirect('/organization/subscription-expired')
    }
}

// Export helper for potential reuse
export { isSubscriptionTrulyExpired, hasValidSubscription, hasPaidAccess }
